import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MAP_ROOT } from '@lassi/config';
import { LatLng, Point } from 'leaflet';
import { finalize } from 'rxjs';
import { MapService } from './map/map.service';
import {
  CoordinatesSearchType,
  CoordinatesType,
  IAdvancedCoordinatesSearchOptions,
} from './models/advanced-search.model';
import { MGAPipe } from './pipes/coordinates.pipe';
import { MGA54CRS, MGA55CRS, VicGridCRS } from './projections';
import { RouteUtilsService } from './route-utils.service';
import { SelectFromMapService } from './select-from-map.service';

@Injectable({
  providedIn: 'root',
})
export class CoordinateSearchService {
  private readonly mapService = inject(MapService);
  private readonly selectFromMapService = inject(SelectFromMapService);
  private readonly router = inject(Router);
  private readonly routeUtilsService = inject(RouteUtilsService);
  private readonly mapRoot = inject(MAP_ROOT);

  public parseLocation(
    coordinateType: CoordinatesType,
    eastingOrLatitude: string,
    northingOrLongitude: string,
  ): LatLng | null {
    if (this.isLatLngModeInCoordsSearch(coordinateType)) {
      // handle latitude and longitude
      return this.mapService.parseLatLng(
        eastingOrLatitude,
        northingOrLongitude,
      );
    } else {
      // handle easting and northing
      const x = parseInt(eastingOrLatitude);
      const y = parseInt(northingOrLongitude);

      if (!x || !y) {
        return null;
      }

      const crs = this.getCRSByCoordinateSearchType(coordinateType);

      return crs.unproject(new Point(x, y));
    }
  }

  /**
   * Coordinate searching related methods
   */
  public startGetCoordinateFromMap(
    initCoordinateSearchType: CoordinatesSearchType,
    mapRadius: number,
  ) {
    this.selectFromMapService
      .selectFromMap()
      .pipe(finalize(() => this.mapService.resetMapMode()))
      .subscribe((event) => {
        const pos = event.latlng;
        if (pos) {
          let eastingOrLatitude: string;
          let northingOrLongitude: string;
          let coordsType = initCoordinateSearchType;

          if (this.isLatLngModeInCoordsSearch(initCoordinateSearchType)) {
            const { lat, lng } = pos;
            // convert negative lat to positive for display
            eastingOrLatitude = Math.abs(lat).toString().slice(0, 16);
            northingOrLongitude = lng.toString().slice(0, 16);
          } else {
            // if handling MGA, need to decide 54 or 55 based on lng
            const { easting, northing, currentCoordsType } =
              this.convertToEastingNorthing(pos);
            eastingOrLatitude = easting.toString();
            northingOrLongitude = northing.toString();
            coordsType = currentCoordsType;
          }

          const updatedOptions: IAdvancedCoordinatesSearchOptions = {
            coordinateSearchType: coordsType,
            eastingOrLatitude,
            northingOrLongitude,
            mapRadius,
          };

          if (this.routeUtilsService.isDrawerActive()) {
            // if in desktop: drawer is active
            // Update the query params only is fine
            this.updateQueryParams(updatedOptions);
          } else {
            // if in the mobile, redirect back to coords search form with params
            this.goBackToCoordinateSearchForm(updatedOptions);
          }
        }
      });
  }

  private convertToEastingNorthing(pos: LatLng): {
    easting: number;
    northing: number;
    currentCoordsType: CoordinatesSearchType;
  } {
    const result = new MGAPipe().transform(pos);
    const easting = Math.floor(result.point.x);
    const northing = Math.floor(result.point.y);
    const currentMgaCoordType =
      result.name === '54'
        ? CoordinatesSearchType.MGA2020_ZONE_54
        : CoordinatesSearchType.MGA2020_ZONE_55;
    return {
      easting,
      northing,
      currentCoordsType: currentMgaCoordType,
    };
  }

  getCRSByCoordinateSearchType(type: CoordinatesType) {
    switch (type) {
      case CoordinatesSearchType.MGA2020_ZONE_54:
      case '54':
        return MGA54CRS;
      case CoordinatesSearchType.MGA2020_ZONE_55:
      case '55':
        return MGA55CRS;
      case CoordinatesSearchType.GDA2020_GEOGRAPHICALS:
        return VicGridCRS;
      default:
        throw new Error('Unknown CRS');
    }
  }

  public isLatLngModeInCoordsSearch(
    coordinateSearchType: CoordinatesType,
  ): boolean {
    return coordinateSearchType === CoordinatesSearchType.GDA2020_GEOGRAPHICALS;
  }

  private updateQueryParams(options: IAdvancedCoordinatesSearchOptions): void {
    this.router.navigate([], {
      queryParamsHandling: 'merge',
      queryParams: {
        searchByCoordinatesFormData: JSON.stringify(options),
      },
    });
  }

  private goBackToCoordinateSearchForm(
    options: IAdvancedCoordinatesSearchOptions,
  ): void {
    this.router.navigate(
      [
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'search',
              'coordinates-search',
            ],
          },
        },
      ],
      {
        queryParamsHandling: 'merge',
        queryParams: {
          searchByCoordinatesFormData: JSON.stringify(options),
        },
      },
    );
  }
}
